import AddBoxIcon from "@mui/icons-material/AddBox";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import SelectedVendorTeamLeader from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team-leader/selected-vendor-team-leader";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));

export type DirectProposalButtonProps = {
  targetTeamId?: Guid;
  targetUserId?: Guid;
  targetEntityId?: Guid;
  targetName?: Name;
  targetHasSigningAuthority?: boolean;
  useStyledButton?: boolean;
  targetTeamName?: string;
  onClick: (targetUrl: URL) => void;
};

export type DirectProposalTarget = Omit<DirectProposalButtonProps, "onClick" | "useStyledButton"> & {
  targetName: Name;
};

/**
 * Where creating a proposal for a team or a vendor rep starts: a new draft addressed to it. A client
 * who wants several firms to bid sends that draft to them from the draft (#6464). Undefined when
 * there is no team and no whole rep to address.
 */
export function getDirectProposalTargetUrl(target: DirectProposalTarget): URL | undefined {
  const { targetTeamId, targetTeamName, targetUserId, targetEntityId, targetName, targetHasSigningAuthority } = target;
  if (targetTeamId) {
    return new SelectedVendorTeam(targetTeamId, targetTeamName).generateTargetUrl(globalThis.location);
  }
  if (!targetUserId || !targetEntityId) return undefined;
  const rep = new SelectedVendorTeamLeader({
    userId: targetUserId,
    legalEntityId: targetEntityId,
    hasSigningAuthority: targetHasSigningAuthority ?? false,
    name: targetName,
  });
  return rep.generateTargetUrl(globalThis.location);
}

export default function DirectProposalButton(props: Readonly<DirectProposalButtonProps>) {
  const { targetName, useStyledButton, onClick } = props;

  if (!targetName) {
    throw new Error("Target name is required when targeting a team leader.");
  }

  const handleClick = () => {
    const targetUrl = getDirectProposalTargetUrl({ ...props, targetName });
    if (targetUrl) onClick(targetUrl);
  };

  return (
    <>
      {useStyledButton && (
        <StyledButton variant="outlined" startIcon={<AddBoxIcon />} onClick={handleClick}>
          Create Proposal
        </StyledButton>
      )}
      {!useStyledButton && (
        <Button color="primary" startIcon={<AddBoxIcon />} onClick={handleClick}>
          Create Proposal
        </Button>
      )}
    </>
  );
}
